import styles from 'css/src/components/animated-explainer-videos/AnimationMinutesIntroModal.module.scss';

import React, { useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';

import { shouldShowAnimationMinutesIntroModal } from '../utils/billingUtils';

import { BillingCycleEnum, Urls } from 'js/src/client';
import { BUTTON_SIZES, PrimaryButton } from 'js/src/components/common/ui/Button';
import Modal, { MODAL_SIZES } from 'js/src/components/common/ui/modal';
import { useDismissCookie } from 'js/src/data/media/cookie-hooks';
import { loadUsageLimits } from 'js/src/data/usage-limit/usage-limit-operations';
import { getUsageLimits } from 'js/src/data/usage-limit/usage-limit-selectors';
import { COOKIE_KEYS } from 'js/src/libs/consts';
import { pluralize } from 'js/src/libs/custom-utils';
import Navigation from 'js/src/libs/navigation';
import { useAppDispatch } from 'js/src/libs/RootState';

const INTRO_IMAGE_URL = 'https://storage.googleapis.com/lumen5-prod-images/mins_man_v3.png';

const AnimationMinutesIntroModal: React.FC = () => {
    const dispatch = useAppDispatch();
    const usageLimit = useSelector(getUsageLimits);
    const [isDismissed, onDismiss] = useDismissCookie({
        cookieKey: COOKIE_KEYS.GENX_ANIMATION_MINUTES_INTRO_SEEN,
    });

    const eligible = shouldShowAnimationMinutesIntroModal();

    useEffect(() => {
        if (eligible && !isDismissed) {
            dispatch(loadUsageLimits());
        }
    }, [dispatch, eligible, isDismissed]);

    const handleDismiss = useCallback(() => {
        onDismiss();
    }, [onDismiss]);

    const handleCtaClick = useCallback(() => {
        analytics.track('GenX Animation Minutes Intro Modal CTA Clicked');
        // Mark as shown so the modal doesn't reappear after the user engages with it.
        onDismiss();
        Navigation.goToLocation(Urls.ANIMATION);
    }, [onDismiss]);

    const secondsPerCycle = usageLimit?.animation_seconds_limit_per_cycle;

    // Wait for usage limits to load before showing, so we never flash "0 minutes".
    if (
        !eligible ||
        isDismissed ||
        secondsPerCycle === null ||
        secondsPerCycle === undefined ||
        secondsPerCycle <= 0
    ) {
        return null;
    }

    const planName = SERVER.USER.plan_display_name || 'paid';
    const isAnnual = usageLimit?.usage_cycle === BillingCycleEnum.ANNUAL;
    const minutesPerCycle = Math.round(secondsPerCycle / 60);
    const period = isAnnual ? 'year' : 'month';
    const minutesLabel = `${minutesPerCycle} animation ${pluralize(minutesPerCycle, 'minute')} per ${period}.`;

    return (
        <Modal showing size={MODAL_SIZES.DEFAULT} onHidden={handleDismiss}>
            <div className="d-flex flex-column align-items-center text-center pb-l5-8">
                <img
                    src={INTRO_IMAGE_URL}
                    alt=""
                    loading="lazy"
                    className={`mb-l5-8 ${styles['intro-modal__image']}`}
                />
                <h4 className="mb-l5-12">Introducing animation minutes</h4>
                <p className="text-secondary mb-l5-16">
                    As a {planName} plan customer, you now have access to
                </p>
                <div
                    className={`w-100 fw-bold rounded-3 p-l5-16 mb-l5-32 ${styles['intro-modal__pill']}`}
                >
                    {minutesLabel}
                </div>

                <ul className="list-unstyled w-100 mb-l5-32">
                    <li className="d-flex align-items-start text-start mb-l5-24">
                        <i
                            className={`material-symbols-outlined me-l5-8 ${styles['intro-modal__feature-icon']} ${styles['intro-modal__feature-icon--image']}`}
                        >
                            imagesmode
                        </i>
                        <div>
                            <span className="fw-bold">AI image generation is now free!</span>
                            <span className="d-block small text-secondary">
                                Generate images in the AI media tab anytime, no minutes needed.
                            </span>
                        </div>
                    </li>
                    <li className="d-flex align-items-start text-start">
                        <i
                            className={`material-symbols-outlined me-l5-8 ${styles['intro-modal__feature-icon']} ${styles['intro-modal__feature-icon--minutes']}`}
                        >
                            search_activity
                        </i>
                        <div>
                            <span className="fw-bold">
                                Animation minutes replaces AI media credits
                            </span>
                            <span className="d-block small text-secondary">
                                Used when generating Animated Explainers or video in the AI media
                                tab.
                            </span>
                        </div>
                    </li>
                </ul>

                <p className="mb-l5-24">We can&apos;t wait to see your creations!</p>

                <PrimaryButton
                    text="Try an animated explainer"
                    size={BUTTON_SIZES.MD}
                    onClick={handleCtaClick}
                />
            </div>
        </Modal>
    );
};

export default AnimationMinutesIntroModal;
